import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { fetchData } from '../api/publicService';
import {
  Box, Container, Typography, Grid, Card, CardMedia, CardContent,
  CircularProgress, Alert, Chip, Stack, Fade,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { IconUser, IconCalendar, IconTag } from '@tabler/icons-react';

const PALETTE = {
  stoneWhite: '#FCF9F3', peacockTeal: '#005B5C', saffronOrange: '#FCA510',
  textPrimary: '#1A2027', textSecondary: '#5F5F5F',
};

const BlogCard = styled(Card)(({ theme }) => ({
  height: '100%', display: 'flex', flexDirection: 'column', borderRadius: '16px',
  boxShadow: '0 8px 24px rgba(0,0,0,0.05)',
  transition: 'transform 0.4s cubic-bezier(0.25, 0.8, 0.25, 1), box-shadow 0.4s cubic-bezier(0.25, 0.8, 0.25, 1)',
  position: 'relative',
  '&:hover': {
    transform: 'translateY(-8px)', boxShadow: '0 16px 32px rgba(0,0,0,0.1)',
  },
}));

const formatDate = (dateString, locale) => {
  if (!dateString) return '';
  const options = { year: 'numeric', month: 'long', day: 'numeric' };
  return new Date(dateString).toLocaleDateString(locale, options);
};

const Blogs = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { language } = useLanguage();
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('All');

  useEffect(() => {
    const loadPosts = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await fetchData('/blogs', language);
        if (Array.isArray(data)) {
          setPosts(data);
          const uniqueCategories = ['All', ...new Set(data.map(p => p.category).filter(Boolean))];
          setCategories(uniqueCategories);
        } else {
          throw new Error("API did not return an array.");
        }
      } catch (err) {
        setError('Could not fetch blog posts. Please try again later.');
        console.error("Fetch Error:", err);
      } finally {
        setLoading(false);
      }
    };
    loadPosts();
  }, [language]);

  const filteredPosts = useMemo(() => {
    if (selectedCategory === 'All') return posts;
    return posts.filter(post => post.category === selectedCategory);
  }, [posts, selectedCategory]);

  const featuredPost = filteredPosts[0];
  const regularPosts = filteredPosts.slice(1);

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', p: 10 }}><CircularProgress size={40} sx={{ color: PALETTE.peacockTeal }} /></Box>;
  }

  if (error) {
    return <Container sx={{ py: 5 }}><Alert severity="error">{error}</Alert></Container>;
  }

  return (
    <Box sx={{ backgroundColor: PALETTE.stoneWhite }}>
      <Box sx={{
        height: '100vh', position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white',
        '&::before': {
          content: '""', position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
          backgroundImage: `linear-gradient(rgba(0,0,0,0.6), rgba(0,0,0,0.2)), url(${featuredPost?.featuredImage || 'default-hero-image.jpg'})`,
          backgroundSize: 'cover', backgroundPosition: 'center', zIndex: 1,
        },
      }}>
        <Container sx={{ position: 'relative', zIndex: 2, textAlign: 'center' }}>
          <Typography variant="h2" component="h1" sx={{ fontFamily: '"Playfair Display", serif', fontWeight: 700, textShadow: '2px 2px 8px rgba(0,0,0,0.7)' }}>
            From Our Journal
          </Typography>
          <Typography variant="h6" sx={{ mt: 2, maxWidth: '600px', mx: 'auto', textShadow: '1px 1px 4px rgba(0,0,0,0.7)', opacity: 0.9 }}>
            Stories, insights, and expert tips to inspire your next Sri Lankan adventure.
          </Typography>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 8 } }}>
        <Stack direction="row" spacing={1} justifyContent="center" sx={{ mb: 6, flexWrap: 'wrap', gap: 1 }}>
          {categories.map((category) => (
            <Chip key={category} icon={<IconTag size={16} />} label={category} onClick={() => setSelectedCategory(category)}
              variant={selectedCategory === category ? 'filled' : 'outlined'} color="primary"
              sx={{
                cursor: 'pointer',
                bgcolor: selectedCategory === category ? PALETTE.peacockTeal : 'transparent', color: selectedCategory === category ? 'white' : PALETTE.peacockTeal,
                borderColor: PALETTE.peacockTeal, '&:hover': { bgcolor: selectedCategory === category ? '#004243' : 'rgba(0, 91, 92, 0.08)' },
              }}
            />
          ))}
        </Stack>

        {featuredPost && (
          <Fade in={!loading} timeout={800}>
            <Box component={Link} to={`/blogs/${featuredPost._id}`} sx={{ textDecoration: 'none', display: 'block', mb: 8 }}>
              <Card sx={{ display: { xs: 'block', md: 'flex' }, boxShadow: '0 16px 40px rgba(0,0,0,0.1)', borderRadius: '16px', transition: 'all 0.3s ease', '&:hover': { transform: 'scale(1.02)' } }}>
                <CardMedia component="img" sx={{ width: { xs: '100%', md: 450 }, height: 300, objectFit: 'cover' }} image={featuredPost.featuredImage} alt={featuredPost.title} />
                <CardContent sx={{ p: 4, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                  <Chip label={featuredPost.category} color="secondary" size="small" sx={{ alignSelf: 'flex-start', mb: 2, bgcolor: PALETTE.saffronOrange, color: 'white' }} />
                  <Typography variant="h4" component="h2" sx={{ fontFamily: '"Playfair Display", serif', fontWeight: 600, color: PALETTE.textPrimary, mb: 2 }}>{featuredPost.title}</Typography>
                  <Typography variant="body1" color={PALETTE.textSecondary} sx={{ mb: 3 }}>{featuredPost.summary || 'Click to read the full story...'}</Typography>
                  <Stack direction="row" spacing={2} color={PALETTE.textSecondary}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}><IconUser size={16} /><Typography variant="caption">{featuredPost.author}</Typography></Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}><IconCalendar size={16} /><Typography variant="caption">{formatDate(featuredPost.createdAt, language)}</Typography></Box>
                  </Stack>
                </CardContent>
              </Card>
            </Box>
          </Fade>
        )}

        <Grid container spacing={4}>
          {regularPosts.map((post) => (
            <Grid item size={{ xs: 12, sm: 6, md: 4 }} key={post._id}>
              <Fade in={!loading} timeout={500} style={{ transitionDelay: '200ms' }}>
                <Link to={`/blogs/${post._id}`} style={{ textDecoration: 'none' }}>
                  <BlogCard>
                    <Box sx={{ position: 'relative' }}>
                      <CardMedia component="img" height="200" image={post.featuredImage} alt={post.title} />
                      <Chip label={post.category} size="small" sx={{ position: 'absolute', top: 12, right: 12, bgcolor: PALETTE.saffronOrange, color: 'white', backdropFilter: 'blur(4px)' }} />
                    </Box>
                    <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', p: 3 }}>
                      <Typography variant="h6" component="h3" fontWeight="600" color={PALETTE.textPrimary} sx={{ flexGrow: 1, mb: 2 }}>{post.title}</Typography>
                      <Stack direction="row" spacing={2} color={PALETTE.textSecondary} sx={{ mt: 'auto' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}><IconUser size={16} /><Typography variant="caption">{post.author}</Typography></Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}><IconCalendar size={16} /><Typography variant="caption">{formatDate(post.createdAt, language)}</Typography></Box>
                      </Stack>
                    </CardContent>
                  </BlogCard>
                </Link>
              </Fade>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

export default Blogs;